import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { changePassword } from "@/api/client";
import { useAdminAuth } from "@/context/AdminAuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

export default function AdminChangePassword() {
  const { admin, mustChange, markPasswordChanged, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      markPasswordChanged();
      toast.success("Password updated");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-amber-300/15 bg-[#080b16]/60 px-4 py-3 text-sm text-amber-50 outline-none transition-colors placeholder:text-amber-100/25 focus:border-amber-300/50";

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#080b16] px-5 text-amber-50">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/4 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(251,191,36,0.06)_1px,transparent_0)] [background-size:26px_26px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-amber-300/15 bg-[#0d1226]/80 p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl sm:p-10"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-amber-300/30 bg-amber-400/10">
            <KeyRound className="h-6 w-6 text-amber-300" />
          </span>
          <p className="text-[11px] uppercase tracking-[0.35em] text-amber-300/70">
            {mustChange ? "First-time setup" : "Security"}
          </p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">
            {mustChange ? "Set your password" : "Change password"}
          </h1>
          <p className="mt-1 text-sm text-amber-100/40">
            {mustChange
              ? "You're signed in with a temporary password. Please set a new one to continue."
              : `Signed in as ${admin?.email}`}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-amber-100/60">
              Current password{mustChange ? " (temporary)" : ""}
            </label>
            <input
              data-testid="change-current-input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className={inputCls}
              placeholder={mustChange ? "krsna1234" : "••••••••"}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-amber-100/60">
              New password
            </label>
            <input
              data-testid="change-new-input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className={inputCls}
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-amber-100/60">
              Confirm new password
            </label>
            <input
              data-testid="change-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              className={inputCls}
              placeholder="Re-enter new password"
            />
          </div>

          {error && (
            <p
              data-testid="change-error"
              className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
            >
              {error}
            </p>
          )}

          <button
            data-testid="change-submit"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-5 py-3 text-sm font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Saving…" : "Update password"}
          </button>

          <button
            type="button"
            onClick={logout}
            className="w-full text-center text-xs text-amber-100/40 transition-colors hover:text-amber-200"
          >
            Sign out
          </button>
        </form>
      </motion.div>
    </div>
  );
}
